import { resolveOrgDefaultDateWindow } from '@saas-foundation/shared-frontend/dateRange';

export const REPORTING_PERIOD_PRESETS = {
  LAST_30: 'last_30',
  LAST_90: 'last_90',
  LAST_180: 'last_180',
  CUSTOM: 'custom',
};

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

export function getDefaultReportingPeriod() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return {
    preset: REPORTING_PERIOD_PRESETS.LAST_30,
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
}

export function normalizeReportingPeriod(period = {}) {
  const preset = period.preset || REPORTING_PERIOD_PRESETS.LAST_30;
  const end = period.endDate ? new Date(`${period.endDate}T12:00:00`) : new Date();
  const start = period.startDate ? new Date(`${period.startDate}T12:00:00`) : new Date(end);

  if (preset === REPORTING_PERIOD_PRESETS.LAST_30) {
    const rangeEnd = new Date();
    const rangeStart = new Date();
    rangeStart.setDate(rangeEnd.getDate() - 29);
    return {
      preset,
      startDate: toDateInputValue(rangeStart),
      endDate: toDateInputValue(rangeEnd),
    };
  }

  if (preset === REPORTING_PERIOD_PRESETS.LAST_90) {
    const rangeEnd = new Date();
    const rangeStart = new Date();
    rangeStart.setDate(rangeEnd.getDate() - 89);
    return {
      preset,
      startDate: toDateInputValue(rangeStart),
      endDate: toDateInputValue(rangeEnd),
    };
  }

  if (preset === REPORTING_PERIOD_PRESETS.LAST_180) {
    const rangeEnd = new Date();
    const rangeStart = new Date();
    rangeStart.setDate(rangeEnd.getDate() - 179);
    return {
      preset,
      startDate: toDateInputValue(rangeStart),
      endDate: toDateInputValue(rangeEnd),
    };
  }

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return getDefaultReportingPeriod();
  }

  return {
    preset: REPORTING_PERIOD_PRESETS.CUSTOM,
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
}

export function reportingPeriodKey(period) {
  const normalized = normalizeReportingPeriod(period);
  return `${normalized.preset}:${normalized.startDate}:${normalized.endDate}`;
}

export function formatReportingPeriodLabel(period) {
  const normalized = normalizeReportingPeriod(period);
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_30) {
    return 'Last 30 days';
  }
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_90) {
    return 'Last 90 days';
  }
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_180) {
    return 'Last 180 days';
  }
  return `${normalized.startDate} to ${normalized.endDate}`;
}

/** Map org Settings (organizations.settings.rcm) → dashboard reporting period shape. */
export function orgRcmSettingsToReportingPeriod(rcmSettings, resolvedWindow = null) {
  const window = resolvedWindow || resolveOrgDefaultDateWindow(rcmSettings);
  if (window.range === 'custom') {
    return {
      preset: REPORTING_PERIOD_PRESETS.CUSTOM,
      startDate: window.date_from,
      endDate: window.date_to,
    };
  }
  if (window.period_days === 180) {
    return {
      preset: REPORTING_PERIOD_PRESETS.LAST_180,
      startDate: window.date_from,
      endDate: window.date_to,
    };
  }
  if (window.period_days === 90) {
    return {
      preset: REPORTING_PERIOD_PRESETS.LAST_90,
      startDate: window.date_from,
      endDate: window.date_to,
    };
  }
  return {
    preset: REPORTING_PERIOD_PRESETS.LAST_30,
    startDate: window.date_from,
    endDate: window.date_to,
  };
}

export function reportingPeriodToApiParams(period) {
  const normalized = normalizeReportingPeriod(period);
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_30) {
    return { time_range: '30d' };
  }
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_90) {
    return { time_range: '90d' };
  }
  if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_180) {
    return { time_range: '180d' };
  }
  return {
    time_range: 'custom',
    date_from: normalized.startDate,
    date_to: normalized.endDate,
  };
}

/** Per-service dashboard window params (Charge Capture uses date_range + ISO datetimes). */
export function dashboardRangeParamsForService(serviceLabel, period) {
  const normalized = normalizeReportingPeriod(period);

  if (serviceLabel === 'ChargeCapture') {
    if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_30) {
      return { date_range: 'last30days' };
    }
    if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_90) {
      return { date_range: 'last90days' };
    }
    return {
      date_range: 'custom',
      date_from: `${normalized.startDate}T00:00:00`,
      date_to: `${normalized.endDate}T23:59:59`,
    };
  }

  if (serviceLabel === 'ChargeCapture WorkQueue') {
    if (normalized.preset === REPORTING_PERIOD_PRESETS.LAST_90) {
      return { time_range: '90d' };
    }
    if (normalized.preset === REPORTING_PERIOD_PRESETS.CUSTOM) {
      const start = new Date(`${normalized.startDate}T12:00:00`);
      const end = new Date(`${normalized.endDate}T12:00:00`);
      const days = Math.ceil((end - start) / 86400000) + 1;
      if (days <= 7) return { time_range: '7d' };
      if (days <= 30) return { time_range: '30d' };
      return { time_range: '90d' };
    }
    return { time_range: '30d' };
  }

  // EDI /monitoring/metrics only accepts organization_id; extra range params can 422/502 on some gateways.
  if (serviceLabel === 'ClearingHouse') {
    return {};
  }

  if (serviceLabel === 'ARPosting') {
    if (
      normalized.preset === REPORTING_PERIOD_PRESETS.CUSTOM
      || normalized.preset === REPORTING_PERIOD_PRESETS.LAST_180
    ) {
      return {
        start_date: normalized.startDate,
        end_date: normalized.endDate,
      };
    }
    return {};
  }

  return reportingPeriodToApiParams(period);
}
